import React, { useState, useMemo, useRef } from 'react';
import b2bService from '../../services/b2bService';
import './B2bPortalBookOrderPage.css';

/**
 * Robust CSV string parser handling quotes, escaped quotes and commas.
 */
function parseCsvText(text) {
  const lines = [];
  let currentLine = [];
  let currentCell = '';
  let insideQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const nextChar = text[i + 1];

    if (char === '"') {
      if (insideQuotes && nextChar === '"') {
        currentCell += '"';
        i++; // skip escaped quote
      } else {
        insideQuotes = !insideQuotes;
      }
    } else if (char === ',' && !insideQuotes) {
      currentLine.push(currentCell.trim());
      currentCell = '';
    } else if ((char === '\r' || char === '\n') && !insideQuotes) {
      if (char === '\r' && nextChar === '\n') i++; // CRLF
      currentLine.push(currentCell.trim());
      currentCell = '';
      if (currentLine.some((c) => c !== '')) {
        lines.push(currentLine);
      }
      currentLine = [];
    } else {
      currentCell += char;
    }
  }

  if (currentCell !== '' || currentLine.length > 0) {
    currentLine.push(currentCell.trim());
    if (currentLine.some((c) => c !== '')) {
      lines.push(currentLine);
    }
  }

  return lines;
}

export default function B2bBulkBookingPanel({ availableTests = [], partyCode = '', partyName = '' }) {
  const [file, setFile] = useState(null);
  const [batchName, setBatchName] = useState('');
  const [parsing, setParsing] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [validationError, setValidationError] = useState(null);

  // Filter for preview table
  const [statusFilter, setStatusFilter] = useState('ALL'); // ALL | VALID | ERROR

  // Test Catalogue Reference Modal
  const [showTestCatalogModal, setShowTestCatalogModal] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState('');

  // Commit Execution State
  const [committing, setCommitting] = useState(false);
  const [commitResult, setCommitResult] = useState(null);
  const [commitError, setCommitError] = useState(null);

  const fileInputRef = useRef(null);

  // 1. Download Pre-Formatted CSV Template
  const handleDownloadTemplate = () => {
    const headers = [
      'SrNo',
      'Title',
      'PatientName',
      'Gender',
      'AgeYears',
      'Mobile',
      'Email',
      'DoctorName',
      'EmployeeCode',
      'TestCodes',
      'Remarks'
    ];

    // Pick 2 sample test codes from active catalogue
    const sampleCode1 = availableTests[0]?.testCode || availableTests[0]?.itemCode || 'CBC';
    const sampleCode2 = availableTests[1]?.testCode || availableTests[1]?.itemCode || 'LFT';

    const sampleRows = [
      ['1', 'Mr.', 'Rajesh Kumar Sharma', 'Male', '34', '9876543210', 'rajesh.sharma@example.com', 'Dr. Mehta', 'EMP-1001', `"${sampleCode1}, ${sampleCode2}"`, 'Camp Day 1'],
      ['2', 'Ms.', 'Pooja Verma', 'Female', '28', '9811223344', 'pooja.verma@example.com', 'Dr. Mehta', 'EMP-1002', `"${sampleCode1}"`, 'Executive Checkup'],
      ['3', 'Mr.', 'Amitabh Sen', 'Male', '45', '9822334455', '', 'Dr. Mehta', 'EMP-1003', `"${sampleCode1}, ${sampleCode2}"`, '']
    ];

    const csvContent = '\uFEFF' + [headers.join(','), ...sampleRows.map((r) => r.join(','))].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `b2b_health_camp_booking_template_${partyCode || 'portal'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // 2. Handle File Upload & Validation
  const handleFileUpload = (e) => {
    const uploadedFile = e.target.files?.[0];
    if (!uploadedFile) return;

    if (!uploadedFile.name.toLowerCase().endsWith('.csv') && !uploadedFile.name.toLowerCase().endsWith('.txt')) {
      setValidationError('Please upload a valid .csv file.');
      return;
    }

    setFile(uploadedFile);
    setValidationError(null);
    setCommitResult(null);
    setCommitError(null);
    setParsing(true);

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const text = event.target?.result;
        if (typeof text !== 'string') {
          throw new Error('Unable to read file content.');
        }

        const lines = parseCsvText(text);
        if (lines.length < 2) {
          throw new Error('The uploaded file is empty or missing data rows.');
        }

        // Header mapping (case-insensitive)
        const headerRow = lines[0].map((h) => h.toLowerCase().replace(/[^a-z0-9]/g, ''));
        const findCol = (...candidates) => {
          return headerRow.findIndex((h) => candidates.some((c) => h.includes(c)));
        };

        const colIdx = {
          srNo: findCol('srno', 'sr', 'sno', 'slno'),
          title: findCol('title', 'prefix'),
          name: findCol('patientname', 'name', 'pname', 'fullname'),
          gender: findCol('gender', 'sex'),
          age: findCol('ageyears', 'age', 'years'),
          mobile: findCol('mobile', 'phone', 'contact', 'cell'),
          email: findCol('email', 'mail'),
          doctor: findCol('doctorname', 'doctor', 'referrer'),
          empCode: findCol('employeecode', 'empcode', 'empid', 'clientorder', 'orderid'),
          tests: findCol('testcodes', 'testcode', 'tests', 'investigations', 'package')
        };

        if (colIdx.name === -1 || colIdx.mobile === -1) {
          throw new Error('Required column "PatientName" or "Mobile" was not found in the CSV header.');
        }

        // Parse data rows
        const rows = [];
        for (let i = 1; i < lines.length; i++) {
          const rowData = lines[i];
          if (!rowData || rowData.length === 0 || rowData.every((c) => !c)) continue;

          const rawTests = colIdx.tests !== -1 && rowData[colIdx.tests] ? rowData[colIdx.tests] : '';
          const testCodes = rawTests
            .split(/[,;/]/)
            .map((t) => t.trim())
            .filter(Boolean);

          const rawAge = colIdx.age !== -1 && rowData[colIdx.age] ? parseInt(rowData[colIdx.age], 10) : null;

          rows.push({
            rowIndex: i,
            title: colIdx.title !== -1 && rowData[colIdx.title] ? rowData[colIdx.title] : 'Mr.',
            patientName: colIdx.name !== -1 && rowData[colIdx.name] ? rowData[colIdx.name] : '',
            gender: colIdx.gender !== -1 && rowData[colIdx.gender] ? rowData[colIdx.gender] : 'Male',
            ageYears: isNaN(rawAge) ? null : rawAge,
            mobile: colIdx.mobile !== -1 && rowData[colIdx.mobile] ? rowData[colIdx.mobile] : '',
            email: colIdx.email !== -1 && rowData[colIdx.email] ? rowData[colIdx.email] : null,
            doctorName: colIdx.doctor !== -1 && rowData[colIdx.doctor] ? rowData[colIdx.doctor] : 'Corporate Camp Referral',
            employeeCode: colIdx.empCode !== -1 && rowData[colIdx.empCode] ? rowData[colIdx.empCode] : null,
            testCodes
          });
        }

        if (rows.length === 0) {
          throw new Error('No valid patient rows found in the CSV file.');
        }

        setParsing(false);
        setValidating(true);

        // Submit to Server for Rate, Contract & Duplicate Validation
        const res = await b2bService.bulkValidateOrders(rows);
        setValidationResult(res);
        if (!batchName) {
          setBatchName(`${uploadedFile.name.replace(/\.[^/.]+$/, '')}`);
        }
      } catch (err) {
        setValidationError(err.response?.data?.message || err.message || 'Error parsing CSV file.');
      } finally {
        setParsing(false);
        setValidating(false);
      }
    };

    reader.readAsText(uploadedFile);
  };

  // 3. Commit Valid Orders Batch
  const handleCommitBatch = async () => {
    if (!validationResult || validationResult.validRowsCount === 0) return;

    // Filter only valid rows
    const validRowsToCommit = validationResult.rowResults
      .filter((r) => r.isValid)
      .map((r) => {
        return {
          rowIndex: r.rowIndex,
          patientId: r.existingPatientId || null,
          patientName: r.patientName,
          gender: r.gender,
          ageYears: r.ageYears,
          mobile: r.mobile,
          doctorName: 'Corporate Camp Referral',
          investigationIds: r.resolvedInvestigationIds
        };
      });

    setCommitting(true);
    setCommitError(null);

    try {
      const res = await b2bService.bulkBookOrders(batchName || 'Corporate Health Camp', validRowsToCommit);
      setCommitResult(res);
    } catch (err) {
      setCommitError(err.response?.data?.message || err.message || 'Batch booking failed. Please check credit and try again.');
    } finally {
      setCommitting(false);
    }
  };

  // 4. Download Confirmation CSV with generated Lab Numbers
  const handleDownloadConfirmation = () => {
    if (!commitResult?.results) return;

    const headers = ['RowNo', 'LabNumber', 'UHID', 'PatientName', 'TestsCount', 'NetAmount', 'Status'];
    const rows = commitResult.results.map((r) => [
      r.rowIndex,
      r.ledgerTransactionNo || 'FAILED',
      r.patientId || '',
      `"${r.patientName}"`,
      r.testCount || 0,
      r.netAmount ? r.netAmount.toFixed(2) : '0.00',
      r.success ? 'COMMITTED' : `"${r.errorMessage || 'ERROR'}"`
    ]);

    const csvContent = '\uFEFF' + [headers.join(','), ...rows.map((r) => r.join(','))].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `b2b_booking_confirmation_${commitResult.batchTrackingId || 'batch'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Filtered rows for inspection grid
  const filteredRows = useMemo(() => {
    if (!validationResult?.rowResults) return [];
    if (statusFilter === 'VALID') return validationResult.rowResults.filter((r) => r.isValid);
    if (statusFilter === 'ERROR') return validationResult.rowResults.filter((r) => !r.isValid);
    return validationResult.rowResults;
  }, [validationResult, statusFilter]);

  // Test Catalogue modal search
  const filteredCatalog = useMemo(() => {
    if (!catalogSearch.trim()) return availableTests;
    const q = catalogSearch.toLowerCase();
    return availableTests.filter(
      (t) =>
        (t.testName && t.testName.toLowerCase().includes(q)) ||
        (t.itemName && t.itemName.toLowerCase().includes(q)) ||
        (t.testCode && t.testCode.toLowerCase().includes(q)) ||
        (t.itemCode && t.itemCode.toLowerCase().includes(q))
    );
  }, [availableTests, catalogSearch]);

  const handleReset = () => {
    setFile(null);
    setValidationResult(null);
    setValidationError(null);
    setCommitResult(null);
    setCommitError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="b2b-bulk-panel" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Informational Guidance Callout */}
      <div
        style={{
          background: 'linear-gradient(135deg, #f0fdf4 0%, #e0f2fe 100%)',
          border: '1px solid #bae6fd',
          borderRadius: '12px',
          padding: '18px 22px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px'
        }}
      >
        <div>
          <h3 style={{ margin: '0 0 6px 0', fontSize: '16px', color: '#0369a1', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>🏢</span> Bulk Patient Booking &amp; Corporate Health Camps
          </h3>
          <p style={{ margin: 0, fontSize: '13px', color: '#334155', lineHeight: '1.4' }}>
            Upload CSV with employee or patient records. The system validates all columns, maps contracted test rates,
            reuses existing patient UHIDs to prevent duplicate medical records, and generates all Lab Numbers simultaneously.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="b2b-btn b2b-btn-secondary"
            onClick={() => setShowTestCatalogModal(true)}
            style={{ background: '#ffffff', borderColor: '#0284c7', color: '#0284c7' }}
          >
            📋 View Test Codes ({availableTests.length})
          </button>
          <button
            type="button"
            className="b2b-btn b2b-btn-primary"
            onClick={handleDownloadTemplate}
          >
            📥 Download Excel/CSV Template
          </button>
        </div>
      </div>

      {/* Upload Zone Card (Visible when not committed) */}
      {!commitResult && (
        <div className="b2b-book-card">
          <h4 className="b2b-card-title">
            <span>📁 Upload Patient Checkup Sheet</span>
            {file && (
              <button
                type="button"
                className="b2b-btn b2b-btn-secondary b2b-btn-sm"
                onClick={handleReset}
              >
                Clear &amp; Re-Upload
              </button>
            )}
          </h4>

          {validationError && (
            <div className="b2b-error-alert" role="alert">
              <strong>Validation Error:</strong> {validationError}
            </div>
          )}

          {!validationResult && (
            <div
              style={{
                border: '2px dashed #cbd5e1',
                borderRadius: '10px',
                padding: '36px 20px',
                textAlign: 'center',
                background: '#f8fafc',
                cursor: 'pointer',
                transition: 'all 0.15s'
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept=".csv,text/csv,text/plain"
                onChange={handleFileUpload}
              />
              <div style={{ fontSize: '36px', marginBottom: '8px' }}>📄</div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>
                {parsing ? 'Parsing CSV File...' : validating ? 'Validating Rates & Demographics with Server...' : 'Click or Drag & Drop Corporate Camp CSV Here'}
              </div>
              <div style={{ fontSize: '12.5px', color: '#64748b' }}>
                Supports standard comma-separated values (.csv) with up to 1000 patients per batch.
              </div>
            </div>
          )}

          {/* Validation Metrics Banner */}
          {validationResult && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: '12px'
                }}
              >
                <div style={{ background: '#f8fafc', padding: '12px 16px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>Total Records</div>
                  <div style={{ fontSize: '22px', fontWeight: 800, color: '#0f172a' }}>{validationResult.totalRows}</div>
                </div>

                <div style={{ background: '#ecfdf5', padding: '12px 16px', borderRadius: '8px', border: '1px solid #a7f3d0' }}>
                  <div style={{ fontSize: '11px', color: '#065f46', textTransform: 'uppercase', fontWeight: 600 }}>Ready to Book</div>
                  <div style={{ fontSize: '22px', fontWeight: 800, color: '#059669' }}>{validationResult.validRowsCount}</div>
                </div>

                <div style={{ background: validationResult.errorRowsCount > 0 ? '#fef2f2' : '#f8fafc', padding: '12px 16px', borderRadius: '8px', border: `1px solid ${validationResult.errorRowsCount > 0 ? '#fecaca' : '#e2e8f0'}` }}>
                  <div style={{ fontSize: '11px', color: validationResult.errorRowsCount > 0 ? '#991b1b' : '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>Invalid Rows</div>
                  <div style={{ fontSize: '22px', fontWeight: 800, color: validationResult.errorRowsCount > 0 ? '#dc2626' : '#64748b' }}>{validationResult.errorRowsCount}</div>
                </div>

                <div style={{ background: '#f0f9ff', padding: '12px 16px', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                  <div style={{ fontSize: '11px', color: '#0369a1', textTransform: 'uppercase', fontWeight: 600 }}>Estimated Batch Cost</div>
                  <div style={{ fontSize: '22px', fontWeight: 800, color: '#0284c7' }}>₹{validationResult.estimatedBatchTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                </div>
              </div>

              {/* Credit Limit Alert Banner */}
              {!validationResult.hasSufficientCredit ? (
                <div className="b2b-error-alert" style={{ margin: 0 }}>
                  <strong>⚠️ Credit Exhaustion Warning:</strong> {validationResult.creditWarningMessage}
                  <div style={{ marginTop: '4px', fontSize: '12px' }}>
                    Available Credit: ₹{validationResult.availableCredit.toLocaleString('en-IN', { minimumFractionDigits: 2 })} &bull; Please top up your account before committing.
                  </div>
                </div>
              ) : (
                <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', padding: '10px 14px', borderRadius: '8px', fontSize: '12.5px', color: '#065f46', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>✓</span> <strong>Credit Check Passed:</strong> Facility has sufficient headroom (Available Credit: ₹{validationResult.availableCredit.toLocaleString('en-IN', { minimumFractionDigits: 2 })})
                </div>
              )}

              {/* Filter Pills and Batch Name */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginTop: '4px' }}>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    className={`b2b-btn b2b-btn-sm ${statusFilter === 'ALL' ? 'b2b-btn-primary' : 'b2b-btn-secondary'}`}
                    onClick={() => setStatusFilter('ALL')}
                  >
                    All Rows ({validationResult.totalRows})
                  </button>
                  <button
                    type="button"
                    className={`b2b-btn b2b-btn-sm ${statusFilter === 'VALID' ? 'b2b-btn-success' : 'b2b-btn-secondary'}`}
                    onClick={() => setStatusFilter('VALID')}
                  >
                    Valid Only ({validationResult.validRowsCount})
                  </button>
                  {validationResult.errorRowsCount > 0 && (
                    <button
                      type="button"
                      className={`b2b-btn b2b-btn-sm ${statusFilter === 'ERROR' ? 'b2b-btn-warning' : 'b2b-btn-secondary'}`}
                      style={{ background: statusFilter === 'ERROR' ? '#ef4444' : '', borderColor: '#ef4444', color: statusFilter === 'ERROR' ? '#ffffff' : '#ef4444' }}
                      onClick={() => setStatusFilter('ERROR')}
                    >
                      Errors Only ({validationResult.errorRowsCount})
                    </button>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input
                    type="text"
                    className="b2b-input"
                    style={{ width: '220px', padding: '6px 10px', fontSize: '12.5px' }}
                    placeholder="Batch / Camp Reference Name..."
                    value={batchName}
                    onChange={(e) => setBatchName(e.target.value)}
                  />
                  <button
                    type="button"
                    className="b2b-btn b2b-btn-primary"
                    disabled={committing || validationResult.validRowsCount === 0 || !validationResult.hasSufficientCredit}
                    onClick={handleCommitBatch}
                  >
                    {committing ? 'Booking Orders...' : `🚀 Confirm & Book ${validationResult.validRowsCount} Patients`}
                  </button>
                </div>
              </div>

              {commitError && (
                <div className="b2b-error-alert" role="alert">
                  <strong>Booking Failed:</strong> {commitError}
                </div>
              )}

              {/* Row Inspection Table */}
              <div style={{ maxHeight: '420px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px', textAlign: 'left' }}>
                  <thead style={{ background: '#f8fafc', position: 'sticky', top: 0, zIndex: 1, borderBottom: '1px solid #e2e8f0' }}>
                    <tr>
                      <th style={{ padding: '8px 12px' }}>#</th>
                      <th style={{ padding: '8px 12px' }}>Status</th>
                      <th style={{ padding: '8px 12px' }}>Patient Name</th>
                      <th style={{ padding: '8px 12px' }}>Age/Gender</th>
                      <th style={{ padding: '8px 12px' }}>Mobile</th>
                      <th style={{ padding: '8px 12px' }}>Tests</th>
                      <th style={{ padding: '8px 12px' }}>Est. Cost</th>
                      <th style={{ padding: '8px 12px' }}>Validation Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((r) => (
                      <tr
                        key={r.rowIndex}
                        style={{
                          borderBottom: '1px solid #f1f5f9',
                          background: r.isValid ? (r.isReturningPatient ? '#f0fdf4' : '#ffffff') : '#fef2f2'
                        }}
                      >
                        <td style={{ padding: '8px 12px', fontWeight: 600 }}>{r.rowIndex}</td>
                        <td style={{ padding: '8px 12px' }}>
                          <span
                            style={{
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: 700,
                              background: r.isValid ? '#dcfce7' : '#fee2e2',
                              color: r.isValid ? '#15803d' : '#b91c1c'
                            }}
                          >
                            {r.isValid ? (r.isReturningPatient ? 'REUSE UHID' : 'VALID') : 'INVALID'}
                          </span>
                        </td>
                        <td style={{ padding: '8px 12px', fontWeight: 600 }}>{r.patientName || '—'}</td>
                        <td style={{ padding: '8px 12px' }}>{r.ageYears}Y / {r.gender}</td>
                        <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{r.mobile}</td>
                        <td style={{ padding: '8px 12px' }}>
                          {r.resolvedTestCodes?.length > 0 ? (
                            <span title={r.resolvedTestNames?.join(', ')}>
                              {r.resolvedTestCodes.join(', ')}
                            </span>
                          ) : (
                            <span style={{ color: '#ef4444' }}>None</span>
                          )}
                        </td>
                        <td style={{ padding: '8px 12px', fontWeight: 700 }}>
                          ₹{r.estimatedRowAmount.toFixed(2)}
                        </td>
                        <td style={{ padding: '8px 12px', fontSize: '11.5px' }}>
                          {r.errors.length > 0 && (
                            <div style={{ color: '#dc2626', fontWeight: 600 }}>
                              {r.errors.join(' • ')}
                            </div>
                          )}
                          {r.warnings.length > 0 && (
                            <div style={{ color: '#0369a1' }}>
                              {r.warnings.join(' • ')}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Commit Results & Confirmation Screen */}
      {commitResult && (
        <div className="b2b-book-card" style={{ border: '2px solid #86efac', background: '#f0fdf4' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '14px' }}>
            <div>
              <h3 style={{ margin: '0 0 6px 0', fontSize: '18px', color: '#166534', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>🎉</span> Batch Registration Completed Successfully!
              </h3>
              <p style={{ margin: 0, fontSize: '13px', color: '#334155' }}>
                {commitResult.message} Batch Tracking ID: <strong>{commitResult.batchTrackingId}</strong>
              </p>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="button"
                className="b2b-btn b2b-btn-primary"
                onClick={handleDownloadConfirmation}
              >
                📥 Download Confirmation CSV
              </button>
              <button
                type="button"
                className="b2b-btn b2b-btn-secondary"
                onClick={handleReset}
              >
                + Book Another Batch
              </button>
            </div>
          </div>

          <div style={{ marginTop: '16px', maxHeight: '360px', overflowY: 'auto', background: '#ffffff', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px', textAlign: 'left' }}>
              <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0 }}>
                <tr>
                  <th style={{ padding: '8px 12px' }}>#</th>
                  <th style={{ padding: '8px 12px' }}>Lab Number (Visit)</th>
                  <th style={{ padding: '8px 12px' }}>Patient UHID</th>
                  <th style={{ padding: '8px 12px' }}>Patient Name</th>
                  <th style={{ padding: '8px 12px' }}>Tests</th>
                  <th style={{ padding: '8px 12px' }}>Net Billed</th>
                  <th style={{ padding: '8px 12px' }}>Profile Status</th>
                </tr>
              </thead>
              <tbody>
                {commitResult.results?.map((item) => (
                  <tr key={item.rowIndex} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '8px 12px', fontWeight: 600 }}>{item.rowIndex}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <strong style={{ color: '#0284c7', fontFamily: 'monospace' }}>{item.ledgerTransactionNo}</strong>
                    </td>
                    <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{item.patientId}</td>
                    <td style={{ padding: '8px 12px', fontWeight: 600 }}>{item.patientName}</td>
                    <td style={{ padding: '8px 12px' }}>{item.testCount} tests</td>
                    <td style={{ padding: '8px 12px', fontWeight: 700 }}>₹{item.netAmount?.toFixed(2)}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <span
                        style={{
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: 600,
                          background: item.isExistingPatientReused ? '#e0f2fe' : '#dcfce7',
                          color: item.isExistingPatientReused ? '#0369a1' : '#15803d'
                        }}
                      >
                        {item.isExistingPatientReused ? 'Reused UHID' : 'New Patient'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Available Test Codes Reference Modal */}
      {showTestCatalogModal && (
        <div className="b2b-modal-backdrop" onClick={() => setShowTestCatalogModal(false)}>
          <div className="b2b-modal-window" style={{ maxWidth: '640px' }} onClick={(e) => e.stopPropagation()}>
            <div className="b2b-modal-header">
              <h3 className="b2b-modal-title">📋 Contracted Test Codes Reference</h3>
              <button
                type="button"
                className="b2b-modal-close-btn"
                onClick={() => setShowTestCatalogModal(false)}
              >
                &times;
              </button>
            </div>
            <div className="b2b-modal-body">
              <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>
                Use these Test Codes in your Excel / CSV sheet column <code>TestCodes</code>. Multiple tests can be comma-separated (e.g. <code>CBC, LFT</code>).
              </p>
              <input
                type="text"
                className="b2b-input"
                placeholder="Search test name or code..."
                value={catalogSearch}
                onChange={(e) => setCatalogSearch(e.target.value)}
                autoFocus
              />
              <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', textAlign: 'left' }}>
                  <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0 }}>
                    <tr>
                      <th style={{ padding: '6px 10px' }}>Test Code</th>
                      <th style={{ padding: '6px 10px' }}>Investigation Name</th>
                      <th style={{ padding: '6px 10px' }}>Contract Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCatalog.map((t) => (
                      <tr key={t.investigationId} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '6px 10px' }}>
                          <code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px', fontWeight: 700, color: '#0284c7' }}>
                            {t.testCode || t.itemCode}
                          </code>
                        </td>
                        <td style={{ padding: '6px 10px', fontWeight: 500 }}>{t.testName || t.itemName}</td>
                        <td style={{ padding: '6px 10px', fontWeight: 700 }}>₹{Number(t.rate).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="b2b-modal-footer">
              <button
                type="button"
                className="b2b-btn b2b-btn-primary"
                onClick={() => setShowTestCatalogModal(false)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

